import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Salarie } from '@shared/types/Salarie';
import type { Candidature } from '@shared/types/Recrutement';
import { esc, fmtDate } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';
import { canMutateRecords } from '@application/access/ViewAccessPolicy';
import { uuid } from '@shared/utils/uuid';
import { onExclusive } from '@presentation/shell/viewDom';
import { compterCandidats, kpisRecrutement } from '@domain/rh/RecrutementKpis';
import { CandidatureSchema } from '@shared/validation/RecrutementSchema';
import {
  bindCrudTable,
  crudActions,
  emptyRow,
  loadRegister,
  openRegisterForm,
  removeById,
  saveRegister,
  tenantIdOf,
  upsertById,
  type FieldDef,
} from '@presentation/shell/localCrud';

function guard(can: boolean): boolean {
  if (can) return true;
  ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
  return false;
}

function salarieOptions(salaries: Salarie[]): { value: string; label: string }[] {
  if (salaries.length === 0) return [{ value: '', label: 'Aucun salarié' }];
  return salaries.map((s) => ({ value: s.id, label: `${s.prenom} ${s.nom}` }));
}

function salarieLabel(salaries: Salarie[], id: string): string {
  const s = salaries.find((x) => x.id === id);
  return s ? `${s.prenom} ${s.nom}` : id || '—';
}

export interface SanctionLocal {
  id: string;
  date: string;
  salarie_id: string;
  niveau: string;
  type: string;
  motif: string;
  statut: string;
}

export interface SuspensionLocal {
  id: string;
  salarie_id: string;
  motif: string;
  date_debut: string;
  date_fin: string;
  statut: string;
}

export const sanctionsController: ViewController = async (section, ctx, shell) => {
  const tid = tenantIdOf(ctx);
  const canMutate = canMutateRecords(shell.permission);
  const salaries = await ctx.salaries.list();
  let sanctions = await loadRegister<SanctionLocal>(tid, 'rh.sanctions');
  let suspensions = await loadRegister<SuspensionLocal>(tid, 'rh.suspensions');

  const renderSanctions = (): void => {
    const tbody = section.querySelector('#sanctions-table tbody');
    if (!tbody) return;
    tbody.innerHTML =
      sanctions.length === 0
        ? emptyRow(7, 'Aucune sanction enregistrée.')
        : sanctions
            .map(
              (s) => `<tr>
          <td>${esc(fmtDate(s.date))}</td>
          <td>${esc(salarieLabel(salaries, s.salarie_id))}</td>
          <td>${esc(s.niveau)}</td>
          <td>${esc(s.type)}</td>
          <td>${esc(s.motif)}</td>
          <td><span class="pill">${esc(s.statut)}</span></td>
          <td class="num">${crudActions(s.id)}</td>
        </tr>`,
            )
            .join('');
  };
  const renderSuspensions = (): void => {
    const tbody = section.querySelector('#suspensions-table tbody');
    if (!tbody) return;
    tbody.innerHTML =
      suspensions.length === 0
        ? emptyRow(6, 'Aucune suspension enregistrée.')
        : suspensions
            .map(
              (s) => `<tr>
          <td>${esc(salarieLabel(salaries, s.salarie_id))}</td>
          <td>${esc(s.motif)}</td>
          <td>${esc(fmtDate(s.date_debut))}</td>
          <td>${esc(fmtDate(s.date_fin))}</td>
          <td><span class="pill">${esc(s.statut)}</span></td>
          <td class="num">${crudActions(s.id)}</td>
        </tr>`,
            )
            .join('');
  };
  renderSanctions();
  renderSuspensions();

  const sanctionFields = (): FieldDef[] => [
    { kind: 'date', name: 'date', label: 'Date', required: true },
    { kind: 'select', name: 'salarie_id', label: 'Salarié', options: salarieOptions(salaries) },
    {
      kind: 'select',
      name: 'niveau',
      label: 'Niveau',
      options: [
        { value: '1', label: '1 — Avertissement' },
        { value: '2', label: '2 — Blâme' },
        { value: '3', label: '3 — Mise à pied' },
        { value: '4', label: '4 — Licenciement' },
      ],
    },
    {
      kind: 'text',
      name: 'type',
      label: 'Type',
      required: true,
      placeholder: 'Avertissement écrit',
    },
    { kind: 'text', name: 'motif', label: 'Motif', required: true },
    {
      kind: 'select',
      name: 'statut',
      label: 'Statut',
      options: [
        { value: 'enregistree', label: 'Enregistrée' },
        { value: 'contestee', label: 'Contestée' },
        { value: 'close', label: 'Close' },
      ],
    },
  ];

  const openSanction = (existing?: SanctionLocal): void => {
    if (!guard(canMutate)) return;
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier la sanction' : 'Nouvelle sanction',
      fields: sanctionFields(),
      values: existing,
      onSubmit: async (data) => {
        const row: SanctionLocal = {
          id: existing?.id ?? uuid('sanc'),
          date: String(data['date']),
          salarie_id: String(data['salarie_id'] ?? ''),
          niveau: String(data['niveau']),
          type: String(data['type']),
          motif: String(data['motif']),
          statut: String(data['statut']),
        };
        sanctions = upsertById(sanctions, row);
        await saveRegister(tid, 'rh.sanctions', sanctions);
        renderSanctions();
      },
    });
  };

  const openSuspension = (existing?: SuspensionLocal): void => {
    if (!guard(canMutate)) return;
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier la suspension' : 'Nouvelle suspension',
      fields: [
        { kind: 'select', name: 'salarie_id', label: 'Salarié', options: salarieOptions(salaries) },
        { kind: 'text', name: 'motif', label: 'Motif', required: true },
        { kind: 'date', name: 'date_debut', label: 'Date début', required: true },
        { kind: 'date', name: 'date_fin', label: 'Date fin', required: true },
        {
          kind: 'select',
          name: 'statut',
          label: 'Statut',
          options: [
            { value: 'en_cours', label: 'En cours' },
            { value: 'terminee', label: 'Terminée' },
          ],
        },
      ],
      values: existing,
      onSubmit: async (data) => {
        const row: SuspensionLocal = {
          id: existing?.id ?? uuid('susp'),
          salarie_id: String(data['salarie_id'] ?? ''),
          motif: String(data['motif']),
          date_debut: String(data['date_debut']),
          date_fin: String(data['date_fin']),
          statut: String(data['statut']),
        };
        suspensions = upsertById(suspensions, row);
        await saveRegister(tid, 'rh.suspensions', suspensions);
        renderSuspensions();
      },
    });
  };

  bindCrudTable(
    section.querySelector('#sanctions-table tbody'),
    section,
    (id) => openSanction(sanctions.find((x) => x.id === id)),
    async (id) => {
      sanctions = removeById(sanctions, id);
      await saveRegister(tid, 'rh.sanctions', sanctions);
      renderSanctions();
    },
    canMutate,
  );
  bindCrudTable(
    section.querySelector('#suspensions-table tbody'),
    section,
    (id) => openSuspension(suspensions.find((x) => x.id === id)),
    async (id) => {
      suspensions = removeById(suspensions, id);
      await saveRegister(tid, 'rh.suspensions', suspensions);
      renderSuspensions();
    },
    canMutate,
  );

  const w = window as unknown as Record<string, unknown>;
  w['openSanctionModal'] = () => openSanction();
  w['openSuspensionModal'] = () => openSuspension();
};

export interface EvaluationLocal {
  id: string;
  salarie_id: string;
  periode: string;
  evaluateur: string;
  note: string;
  objectifs?: string;
  points_forts?: string;
  axes_progression?: string;
  decision?: string;
  statut: string;
}

export const evaluationsController: ViewController = async (section, ctx, shell) => {
  const tid = tenantIdOf(ctx);
  const canMutate = canMutateRecords(shell.permission);
  const salaries = await ctx.salaries.list();
  let items = await loadRegister<EvaluationLocal>(tid, 'rh.evaluations');

  const render = (): void => {
    const tbody = section.querySelector('#evaluations-tbody');
    const empty = section.querySelector<HTMLElement>('#evaluations-empty');
    if (empty) empty.style.display = items.length === 0 ? '' : 'none';
    if (!tbody) return;
    tbody.innerHTML =
      items.length === 0
        ? ''
        : items
            .map(
              (e) => `<tr>
          <td>${esc(salarieLabel(salaries, e.salarie_id))}</td>
          <td>${esc(e.periode)}</td>
          <td>${esc(e.evaluateur)}</td>
          <td>${esc(e.note)}</td>
          <td>${esc(e.objectifs || '—')}</td>
          <td>${esc(e.decision || '—')}</td>
          <td><span class="pill">${esc(e.statut)}</span></td>
          <td class="num">${crudActions(e.id)}</td>
        </tr>`,
            )
            .join('');
  };
  render();

  const openEditor = (existing?: EvaluationLocal): void => {
    if (!guard(canMutate)) return;
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier l’évaluation' : 'Nouvelle évaluation',
      fields: [
        { kind: 'select', name: 'salarie_id', label: 'Salarié', options: salarieOptions(salaries) },
        {
          kind: 'text',
          name: 'periode',
          label: 'Période',
          required: true,
          placeholder: '2026',
          pattern: '20\\d{2}',
          patternMessage: 'Veuillez saisir une année valide (ex. : 2026)',
        },
        { kind: 'text', name: 'evaluateur', label: 'Évaluateur', required: true },
        { kind: 'text', name: 'note', label: 'Note', required: true, placeholder: 'A / 16/20' },
        {
          kind: 'text',
          name: 'objectifs',
          label: 'Objectifs évalués',
          required: false,
          placeholder: 'Ex. qualité, délais, collaboration',
        },
        {
          kind: 'text',
          name: 'points_forts',
          label: 'Points forts',
          required: false,
          placeholder: 'Ex. rigueur, autonomie',
        },
        {
          kind: 'text',
          name: 'axes_progression',
          label: 'Axes de progression',
          required: false,
          placeholder: 'Ex. maîtrise Excel avancé',
        },
        {
          kind: 'select',
          name: 'decision',
          label: 'Décision RH',
          options: [
            { value: 'maintien', label: 'Maintien poste' },
            { value: 'promotion', label: 'Promotion' },
            { value: 'formation', label: 'Plan de formation' },
            { value: 'accompagnement', label: 'Accompagnement ciblé' },
          ],
        },
        {
          kind: 'select',
          name: 'statut',
          label: 'Statut',
          options: [
            { value: 'brouillon', label: 'Brouillon' },
            { value: 'validee', label: 'Validée' },
            { value: 'archivee', label: 'Archivée' },
          ],
        },
      ],
      values: existing,
      onSubmit: async (data) => {
        const row: EvaluationLocal = {
          id: existing?.id ?? uuid('eval'),
          salarie_id: String(data['salarie_id'] ?? ''),
          periode: String(data['periode']),
          evaluateur: String(data['evaluateur']),
          note: String(data['note']),
          objectifs: String(data['objectifs'] ?? ''),
          points_forts: String(data['points_forts'] ?? ''),
          axes_progression: String(data['axes_progression'] ?? ''),
          decision: String(data['decision'] ?? ''),
          statut: String(data['statut']),
        };
        items = upsertById(items, row);
        await saveRegister(tid, 'rh.evaluations', items);
        render();
      },
    });
  };

  bindCrudTable(
    section.querySelector('#evaluations-tbody'),
    section,
    (id) => openEditor(items.find((x) => x.id === id)),
    async (id) => {
      items = removeById(items, id);
      await saveRegister(tid, 'rh.evaluations', items);
      render();
    },
    canMutate,
  );

  const w = window as unknown as Record<string, unknown>;
  w['openEvaluationModal'] = () => openEditor();
};

export interface OffreLocal {
  id: string;
  poste: string;
  service: string;
  type: string;
  candidats: number;
  date: string;
  statut: string;
}

export interface CandidatureLocal {
  id: string;
  poste_id: string;
  candidat_nom: string;
  candidat_email?: string;
  date_reception: string;
  statut: string;
  cree_le: string;
}

export const recrutementController: ViewController = async (section, ctx, shell) => {
  const tid = tenantIdOf(ctx);
  const canMutate = canMutateRecords(shell.permission);
  let offres = await loadRegister<OffreLocal>(tid, 'rh.offres');
  let candidatures = await loadRegister<CandidatureLocal>(tid, 'rh.candidatures');

  const render = (): void => {
    const k = kpisRecrutement(
      offres.map((o) => ({
        id: o.id,
        poste: o.poste,
        service: o.service,
        type: o.type,
        date: o.date,
        statut: o.statut,
      })),
      candidatures.map((c) => ({
        id: c.id,
        tenant_id: tid,
        poste_id: c.poste_id,
        candidat_nom: c.candidat_nom,
        candidat_email: c.candidat_email,
        date_reception: c.date_reception,
        statut: c.statut as Candidature['statut'],
        cree_le: c.cree_le,
      })),
    );
    const set = (id: string, v: string): void => {
      const el = section.querySelector(id);
      if (el) el.textContent = v;
    };
    set('#kpi-offres', String(k.ouvertes));
    set('#kpi-candidats', String(k.candidats));
    set('#kpi-entretiens', String(k.entretiens));
    set('#kpi-embauches', String(k.embauchesMois));
    const tbody = section.querySelector('#offres-tbody');
    const empty = section.querySelector<HTMLElement>('#offres-empty');
    if (empty) empty.style.display = offres.length === 0 ? '' : 'none';
    if (!tbody) return;
    tbody.innerHTML =
      offres.length === 0
        ? ''
        : offres
            .map((o) => {
              const n = compterCandidats(
                candidatures.map((c) => ({
                  id: c.id,
                  tenant_id: tid,
                  poste_id: c.poste_id,
                  candidat_nom: c.candidat_nom,
                  date_reception: c.date_reception,
                  statut: c.statut as Candidature['statut'],
                  cree_le: c.cree_le,
                })),
                o.id,
              );
              return `<tr>
          <td>${esc(o.poste)}</td>
          <td>${esc(o.service)}</td>
          <td>${esc(o.type)}</td>
          <td class="num">${n}</td>
          <td>${esc(fmtDate(o.date))}</td>
          <td><span class="pill ${o.statut === 'ouverte' ? 'success' : ''}">${esc(o.statut)}</span></td>
          <td class="num">${crudActions(o.id)} <button type="button" class="btn btn-ghost btn-sm" data-candidats="${esc(o.id)}">Candidats</button></td>
        </tr>`;
            })
            .join('');

    const cBody = section.querySelector('#candidats-tbody');
    if (cBody) {
      cBody.innerHTML =
        candidatures.length === 0
          ? '<tr><td colspan="6" style="color:var(--gris);padding:14px;">Aucune candidature. Ajoutez-en depuis une offre.</td></tr>'
          : candidatures
              .map((c) => {
                const offre = offres.find((o) => o.id === c.poste_id);
                return `<tr>
            <td>${esc(c.candidat_nom)}</td>
            <td>${esc(offre?.poste ?? c.poste_id)}</td>
            <td>${esc(c.candidat_email ?? '—')}</td>
            <td>${esc(fmtDate(c.date_reception))}</td>
            <td><span class="pill">${esc(c.statut)}</span></td>
            <td class="num">${crudActions(c.id)}</td>
          </tr>`;
              })
              .join('');
    }
  };
  render();

  const openEditor = (existing?: OffreLocal): void => {
    if (!guard(canMutate)) return;
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier l’offre' : 'Nouvelle offre',
      fields: [
        { kind: 'text', name: 'poste', label: 'Poste', required: true },
        { kind: 'text', name: 'service', label: 'Service', required: true },
        {
          kind: 'select',
          name: 'type',
          label: 'Type',
          options: [
            { value: 'CDI', label: 'CDI' },
            { value: 'CDD', label: 'CDD' },
            { value: 'stage', label: 'Stage' },
          ],
        },
        { kind: 'date', name: 'date', label: 'Date', required: true },
        {
          kind: 'select',
          name: 'statut',
          label: 'Statut',
          options: [
            { value: 'ouverte', label: 'Ouverte' },
            { value: 'pourvue', label: 'Pourvue' },
            { value: 'annulee', label: 'Annulée' },
          ],
        },
      ],
      values: existing,
      onSubmit: async (data) => {
        const row: OffreLocal = {
          id: existing?.id ?? uuid('offre'),
          poste: String(data['poste']),
          service: String(data['service']),
          type: String(data['type']),
          candidats: 0,
          date: String(data['date']),
          statut: String(data['statut']),
        };
        offres = upsertById(offres, row);
        await saveRegister(tid, 'rh.offres', offres);
        render();
      },
    });
  };

  const openCandidat = (posteId: string, existing?: CandidatureLocal): void => {
    if (!guard(canMutate)) return;
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier la candidature' : 'Nouvelle candidature',
      size: 'lg',
      fields: [
        { kind: 'text', name: 'candidat_nom', label: 'Nom du candidat', required: true },
        { kind: 'text', name: 'candidat_email', label: 'E-mail' },
        { kind: 'date', name: 'date_reception', label: 'Date de réception', required: true },
        {
          kind: 'select',
          name: 'statut',
          label: 'Statut',
          options: [
            { value: 'recue', label: 'Reçue' },
            { value: 'preselection', label: 'Présélection' },
            { value: 'entretien', label: 'Entretien' },
            { value: 'offre', label: 'Offre' },
            { value: 'refusee', label: 'Refusée' },
            { value: 'acceptee', label: 'Acceptée' },
          ],
        },
        {
          kind: 'select',
          name: 'consent_given',
          label: 'Consentement collecte',
          options: [
            { value: '', label: 'Choisir…' },
            { value: 'oui', label: 'Consentement donné' },
            { value: 'non', label: 'Consentement refusé' },
          ],
        },
        {
          kind: 'select',
          name: 'consent_method',
          label: 'Méthode',
          options: [
            { value: 'formulaire_erp', label: 'Formulaire ERP' },
            { value: 'papier_scanné', label: 'Papier scanné' },
            { value: 'email', label: 'Email' },
            { value: 'verbal_retranscrit', label: 'Verbal retranscrit' },
          ],
        },
        {
          kind: 'text',
          name: 'consent_notes',
          label: 'Notes consentement',
          required: false,
          placeholder: 'Ex. consentement papier archivé',
        },
      ],
      values: existing,
      onSubmit: async (data) => {
        const offre = offres.find((o) => o.id === posteId);
        const parsed = CandidatureSchema.safeParse({
          candidat_nom: String(data['candidat_nom'] ?? ''),
          candidat_email: String(data['candidat_email'] ?? ''),
          date_reception: String(data['date_reception'] ?? ''),
          date_offre: offre?.date,
          statut: String(data['statut'] ?? 'recue'),
          consent_given: String(data['consent_given'] ?? ''),
          consent_method: String(data['consent_method'] ?? ''),
          consent_notes: String(data['consent_notes'] ?? ''),
        });
        if (!parsed.success) {
          ToastService.error(
            parsed.error.issues[0]?.message ?? 'Candidature invalide (dates ou champs)',
          );
          throw new Error('candidature_invalide');
        }
        const now = new Date().toISOString();
        const row: CandidatureLocal = {
          id: existing?.id ?? uuid('cand'),
          poste_id: posteId,
          candidat_nom: parsed.data.candidat_nom,
          candidat_email: parsed.data.candidat_email ?? '',
          date_reception: parsed.data.date_reception,
          statut: parsed.data.statut,
          cree_le: existing?.cree_le ?? now,
        };
        const { recordCandidateConsent } = await import(
          '@presentation/shell/controllers/confidentialite'
        );
        await recordCandidateConsent(ctx, row.id, {
          consent_given: parsed.data.consent_given === 'oui',
          consent_method: parsed.data.consent_method as
            | 'formulaire_erp'
            | 'papier_scanné'
            | 'email'
            | 'verbal_retranscrit',
          notes: parsed.data.consent_notes?.trim() || undefined,
        });
        candidatures = upsertById(candidatures, row);
        await saveRegister(tid, 'rh.candidatures', candidatures);
        render();
      },
    });
  };

  bindCrudTable(
    section.querySelector('#offres-tbody'),
    section,
    (id) => openEditor(offres.find((x) => x.id === id)),
    async (id) => {
      offres = removeById(offres, id);
      candidatures = candidatures.filter((c) => c.poste_id !== id);
      await saveRegister(tid, 'rh.offres', offres);
      await saveRegister(tid, 'rh.candidatures', candidatures);
      render();
    },
    canMutate,
  );

  onExclusive(section.querySelector('#offres-tbody'), 'click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-candidats]');
    if (!btn?.dataset.candidats) return;
    openCandidat(btn.dataset.candidats);
  });

  bindCrudTable(
    section.querySelector('#candidats-tbody'),
    section,
    (id) => {
      const c = candidatures.find((x) => x.id === id);
      if (c) openCandidat(c.poste_id, c);
    },
    async (id) => {
      candidatures = removeById(candidatures, id);
      await saveRegister(tid, 'rh.candidatures', candidatures);
      render();
    },
    canMutate,
  );

  const w = window as unknown as Record<string, unknown>;
  w['openOffreModal'] = () => openEditor();
};
